import {
  ActionLabel,
  getPlaceholder,
  getPlaceholderForConversionReportType,
  getPlaceholderForCustomReportType,
  getPlaceholderForPFTrans,
  getPlaceholderForPFFinal,
  getStatus,
  getStatusForConversionReportType,
  getStatusForCustomReportType,
  getStatusForPFTrans,
  getStatusForPFFinal,
  isFromScratch,
  statusKeys
} from '../../../data';
import OutlinedNavigateIcon from '../../../../../../assets/icons/navigate-outlined.svg?react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { jurisdictionService } from '../../../../../../services/jurisdiction.service';
import { useAutomateReportGeneration } from '../../../../../../services/automation.service';
import { CloseIcon } from '../../../../../DataExtraction/components/FundMappingModal/icons';
import toast from 'react-hot-toast';
import { FaArrowRight } from 'react-icons/fa6';
import { REPORT_TYPES } from '../../../../../../consts/reportTypes';
import { useConvertReport } from '../../../../../../services/period.service';
import { TbTransform } from 'react-icons/tb';
import { extractApiError } from '../../../../../../utils';
import { useValidateDetailedOutputStats } from '../../../../../../services/funds.service';
import { isV2Surface } from '../../../../../../utils/design';
import { CriticalIssuesModal } from '../../../../../../components/CriticalIssuesModal';

/* How long the cell keeps its word that a report was asked for, and how often it re-reads the
   period while waiting. Thirty seconds is past the point where a generate that started is going to
   say so; after that the cell would rather show the period's own truth, whatever it is, than go on
   claiming something it can no longer see. */
const ASKED_SETTLE_MS = 30000;
const ASKED_POLL_MS = 4000;

export const ReportColumn = ({
  period,
  workflowId,
  refetch,
  reportType,
  /* The redesigned period strip puts this cell under a caption that already says "Report", where
     "Report Generated" says the word twice and wraps onto two lines in a cell whose neighbours are
     one line. Off by default: the classic table's own column keeps every string it has. */
  compactLabels = false
}) => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  /* When this cell last asked for a report, and nothing yet said the period knows.

     The request coming back means the work was QUEUED, not that it has started: the refetch fired
     right after it can still answer "Not started", and the cell then offers Generate Report a
     second time on a report already being made. The second press is answered 400 "This report was
     already generated" — reported as a duplicate request, and read by the person as a failure on
     top of a press that had worked. So the cell goes on saying "generating" until the period itself
     says the work started, and gives up after a window rather than hanging there for good. */
  const [askedAt, setAskedAt] = useState(null);

  const isFormPF = reportType === REPORT_TYPES.FORM_PF;

  const status = useMemo(() => {
    if (period.filing_type === 'PF_FINAL') return getStatusForPFFinal(period);
    if (period.filing_type === 'PF_TRANS') return getStatusForPFTrans(period);
    if (reportType === REPORT_TYPES.CONVERSION) return getStatusForConversionReportType(period);
    else if (reportType === REPORT_TYPES.CUSTOM) {
      const _status = getStatusForCustomReportType(period);
      return _status;
    }

    return getStatus(period, reportType);
  }, [reportType, period]);

  const isScratch = isFromScratch(period);
  const placeholder = useMemo(() => {
    if (period.filing_type === 'PF_FINAL') return getPlaceholderForPFFinal(period);
    if (period.filing_type === 'PF_TRANS') return getPlaceholderForPFTrans(period);
    if (reportType === REPORT_TYPES.CONVERSION)
      return getPlaceholderForConversionReportType(period);
    else if (reportType === REPORT_TYPES.CUSTOM) return getPlaceholderForCustomReportType(period);

    return getPlaceholder(period, reportType);
  }, [reportType, period]);

  const generateReport = useAutomateReportGeneration();
  const [hasCriticalIssues, setHasCriticalIssues] = useState(false);
  const convertReport = useConvertReport();
  const validateDetailedOutputStats = useValidateDetailedOutputStats();

  /* Whether the period itself now says a report is being made or has been. Either ends the window:
     from here on the cell has something true to show and does not need to remember the press. */
  const reportUnderway =
    period?.report_progress_status === 'Completed' ||
    /in.?progress|completed/i.test(String(period?.report_task?.status ?? ''));

  useEffect(() => {
    if (!askedAt) return undefined;
    if (reportUnderway) {
      setAskedAt(null);
      return undefined;
    }
    /* Asking again rather than waiting: the periods page learns of the change over SignalR, and a
       push that never comes is what left this cell offering the press again in the first place. */
    const tick = window.setInterval(() => {
      if (Date.now() - askedAt >= ASKED_SETTLE_MS) setAskedAt(null);
      else refetch?.();
    }, ASKED_POLL_MS);
    return () => window.clearInterval(tick);
  }, [askedAt, reportUnderway, refetch]);

  const getJurisdictionsList = async () => {
    if (reportType === REPORT_TYPES.CUSTOM) {
      navigate(`/output-tables/${workflowId}/${period?.id}`);

      return;
    }

    /* The redesign has one report page whichever regulator the filing is for, so there is nothing
       to look up before opening it: the classic app asks the server which jurisdiction this period
       reports to only to pick between three addresses. */
    if (isV2Surface()) {
      navigate(`/report-v2/${workflowId}/${period.id}`);

      return;
    }

    setIsLoading(true);

    jurisdictionService
      .getJurisdictions(period?.id)
      .then(res => {
        const el = res?.[0];
        const natCode = el?.reporting_member_state;
        const isFCA = natCode === 'UK' || natCode === 'uk';

        if (isFormPF) {
          navigate(`/report/pf/us/${workflowId}/${period.id}`);
          return;
        }

        if (isFCA) navigate(`/report/fca/uk/${workflowId}/${period.id}`);
        else navigate(`/report/esma/${natCode?.toLowerCase()}/${workflowId}/${period.id}`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onClick = () => {
    getJurisdictionsList();
  };

  const onGenerateReport = () => {
    generateReport.mutate(
      {
        period_id: period?.id
      },
      {
        onSuccess: () => {
          setAskedAt(Date.now());
          refetch();
        },
        onError: err => {
          toast.error(extractApiError(err, 'Unable to generate this report.'));
        }
      }
    );
  };

  const onValidateAndGenerateReport = () => {
    validateDetailedOutputStats.mutate(
      {
        periodId: period?.id
      },
      {
        onSuccess: stats => {
          const hasBlockingIssue = stats?.errors?.length > 0;

          if (hasBlockingIssue) {
            setHasCriticalIssues(true);
            return;
          }

          onGenerateReport();
        },
        onError: err => {
          toast.error(
            extractApiError(err, 'Unable to validate output data before generating the report.')
          );
        }
      }
    );
  };

  const onConvertReport = () => {
    convertReport.mutate(
      {
        periodId: period?.id,
        workflowId: workflowId
      },
      {
        onSuccess: () => {
          setAskedAt(Date.now());
          refetch();
        },
        onError: err => {
          toast.error(extractApiError(err, 'Unable to convert this report.'));
        }
      }
    );
  };

  const criticalModal = (
    <CriticalIssuesModal opened={hasCriticalIssues} onClose={() => setHasCriticalIssues(false)} />
  );

  const onOpenDrawer = () => {
    if (isScratch) onClick();
    else onValidateAndGenerateReport();
  };

  if (isLoading) return <ActionLabel placeholder="Redirecting..." />;

  if (validateDetailedOutputStats.isLoading)
    return <ActionLabel placeholder="Checking output..." />;

  if (generateReport.isLoading || askedAt)
    return <ActionLabel placeholder="Report generating..." />;

  if (reportType === REPORT_TYPES.CUSTOM) {
    if (
      status === statusKeys.FILES ||
      status === statusKeys.EXTRACTION ||
      status === statusKeys.TRANSFORMATION
    )
      return <ActionLabel placeholder={placeholder} />;

    return (
      <ActionLabel
        label="Check Output Data"
        color="#40C057"
        onClick={getJurisdictionsList}
        rightIcon={<FaArrowRight />}
      />
    );
  }

  if (reportType === REPORT_TYPES.CONVERSION) {
    if (
      status !== statusKeys.APPROVAL &&
      status !== statusKeys.SUBMISSION &&
      status !== statusKeys.REPORT &&
      status !== statusKeys.END
    )
      return <ActionLabel placeholder={placeholder} />;

    if (period?.report_progress_status === 'Completed')
      return (
        <ActionLabel
          label="View Report"
          color="#40C057"
          onClick={getJurisdictionsList}
          rightIcon={<FaArrowRight />}
        />
      );

    if ((period?.report_task?.status === 'In progress' && !isScratch) || convertReport.isLoading)
      return <ActionLabel label="Generating report..." />;

    if (period?.report_task?.status === 'Failed' && !isScratch)
      return (
        <>
          <ActionLabel
            label="Failed"
            color="#E03131"
            rightIcon={<CloseIcon size={16} color="#E03131" />}
            onClick={onValidateAndGenerateReport}
          />
          {criticalModal}
        </>
      );

    return (
      <ActionLabel
        noFill
        label="Convert & Generate"
        leftIcon={<TbTransform color="#228BE6" size={16} />}
        onClick={onConvertReport}
      />
    );
  }

  if (period?.report_progress_status === 'Completed')
    return (
      <ActionLabel
        label={compactLabels ? 'Generated' : 'Report Generated'}
        color="#40C057"
        onClick={getJurisdictionsList}
        rightIcon={<FaArrowRight />}
      />
    );

  if (period?.report_task?.status === 'In progress' && !isScratch)
    return <ActionLabel label={compactLabels ? 'Generating…' : 'Generating report...'} />;

  if (period?.report_task?.status === 'Failed' && !isScratch)
    return (
      <>
        <ActionLabel
          label="Failed"
          color="#E03131"
          rightIcon={<CloseIcon size={16} color="#E03131" />}
          onClick={onValidateAndGenerateReport}
        />
        {criticalModal}
      </>
    );

  if (status === statusKeys.REPORT && period?.report_progress_status === 'Not started')
    return (
      <>
        <ActionLabel
          label={compactLabels ? 'Generate' : 'Report generate'}
          rightIcon={<OutlinedNavigateIcon />}
          onClick={onOpenDrawer}
        />
        {criticalModal}
      </>
    );

  if (
    status !== statusKeys.APPROVAL &&
    status !== statusKeys.SUBMISSION &&
    status !== statusKeys.REPORT &&
    status !== statusKeys.END
  )
    return <ActionLabel placeholder={placeholder} />;

  return (
    <ActionLabel
      label={compactLabels ? 'Generated' : 'Report Generated'}
      color="#40C057"
      onClick={getJurisdictionsList}
      rightIcon={<FaArrowRight />}
    />
  );
};
